import React, { useState, useEffect } from "react";
import {
  FaInbox,
  FaSearch,
  FaFilter,
  FaSort,
  FaCheckCircle,
  FaExclamationCircle,
  FaInfoCircle,
  FaClock,
  FaEye,
  FaTrash,
} from "react-icons/fa";
import Sidebar from "../components/SideBar";
import LoadingSpinner from "../components/LoadingSpinner";
import { listenToNotifications } from "./NotificationSection/notificationsFunction";

// 🔹 Notification card component
const MessageCard = ({ message, onView, onDelete, isDeleting }) => {
  const getTypeIcon = (type) => {
    switch (type) {
      case "approval":
      case "success":
        return <FaCheckCircle className="text-green-500" />;
      case "alert":
        return <FaExclamationCircle className="text-red-500" />;
      case "info":
      default:
        return <FaInfoCircle className="text-blue-500" />;
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }

    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    if (diffInHours < 1) {
      const minutes = Math.floor(diffInHours * 60);
      return `${minutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <div
      className={`bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 p-6 ${
        !message.read ? "border-l-4 border-l-blue-500 bg-blue-50/30" : ""
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-4 flex-1">
          <div className="mt-1">{getTypeIcon(message.type)}</div>
          <div className="flex-1 min-w-0">
            <h3
              className={`font-semibold text-gray-900 mb-1 ${
                !message.read ? "font-bold" : ""
              }`}
            >
              {message.title}
            </h3>
            <p className="text-gray-700 text-sm mb-2">{message.message}</p>
            <div className="text-xs text-gray-500 flex items-center space-x-2">
              <FaClock />
              <span>{formatTime(message.createdAt)}</span>
            </div>
          </div>
        </div>
        <div className="flex space-x-1">
          <button
            onClick={() => onView(message.id)}
            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
          >
            <FaEye />
          </button>
          <button
            onClick={() => onDelete(message.id)}
            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
            disabled={isDeleting}
          >
            {isDeleting ? <LoadingSpinner size="w-4 h-4" /> : <FaTrash />}
          </button>
        </div>
      </div>
    </div>
  );
};

// 🔹 Shimmer placeholder card (realistic version)
const NotificationShimmer = () => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
    <div className="flex items-start justify-between">
      <div className="flex items-start space-x-4 flex-1">
        <div className="w-5 h-5 rounded-full bg-gray-200 mt-1" />
        <div className="flex-1 min-w-0 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="h-3 bg-gray-200 rounded w-2/3"></div>
          <div className="h-3 bg-gray-200 rounded w-1/4"></div>
        </div>
      </div>
      <div className="flex space-x-2">
        <div className="w-6 h-6 bg-gray-200 rounded"></div>
        <div className="w-6 h-6 bg-gray-200 rounded"></div>
      </div>
    </div>
  </div>
);

// 🔹 Main Inbox Component
const MessageInbox = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [sortBy, setSortBy] = useState("newest");

  useEffect(() => {
    const unsubscribe = listenToNotifications((data) => {
      setNotifications(data);
      setLoading(false);
    });
    return () => unsubscribe && unsubscribe();
  }, []);

  const handleView = (id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      setDeletingId(null);
    }, 1000);
  };

  const filteredAndSorted = notifications
    .filter((n) => {
      const matchesSearch =
        n.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        n.message?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = filterType === "all" || n.type === filterType;
      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      const getTime = (t) => {
        if (!t) return 0;
        if (t.toDate) return t.toDate().getTime();
        if (t.seconds) return t.seconds * 1000;
        return new Date(t).getTime();
      };
      const timeA = getTime(a.createdAt);
      const timeB = getTime(b.createdAt);
      
      if (sortBy === "newest") return timeB - timeA;
      if (sortBy === "oldest") return timeA - timeB;
      return 0;
    });

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Sidebar />
      <main className="ml-80 p-8 lg:p-10 min-h-screen">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Notifications</h1>
              <p className="text-slate-500 mt-1 text-sm sm:text-base">
                You have {unreadCount} unread notification
                {unreadCount !== 1 && "s"}.
              </p>
            </div>
            <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-xl border border-gray-100 shadow-sm">
              <FaInbox className="text-blue-600" />
              <span className="font-semibold text-slate-700 text-sm">
                Total: {notifications.length}
              </span>
            </div>
          </div>

        {/* Filters (always visible) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6 flex flex-wrap gap-4 items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search notifications..."
              className="pl-10 pr-4 py-3 w-full border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="flex items-center space-x-2">
            <FaFilter className="text-gray-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              <option value="all">All Types</option>
              <option value="approval">Approval</option>
              <option value="info">Info</option>
              <option value="alert">Alert</option>
            </select>

            <FaSort className="text-gray-400" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
            </select>
          </div>
        </div>

        {/* Notifications List */}
        {loading ? (
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <NotificationShimmer key={i} />
            ))}
          </div>
        ) : filteredAndSorted.length ? (
          <div className="space-y-4">
            {filteredAndSorted.map((n) => (
              <MessageCard
                key={n.id}
                message={n}
                onView={handleView}
                onDelete={handleDelete}
                isDeleting={deletingId === n.id}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <FaInbox className="mx-auto text-6xl text-gray-300 mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              No notifications found
            </h3>
            <p className="text-gray-500">
              {searchQuery
                ? "Try changing your search or filters."
                : "You're all caught up!"}
            </p>
          </div>
        )}
        </div>
      </main>
    </div>
  );
};

export default MessageInbox;
